import { useMemo } from "react";

import { getFlattenedData } from "@shared/utils/data/data-utils";

import usePublicTTDiscountPermissionService from "@shared/modules/ticket-types/data/services/usePublicTTDiscountPermissionService";

import useUserBasedDiscountService from "@app-contexts/public/features/discounts/data/services/useUserBasedDiscountService";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";

export default function usePublicApplicableTicketCredits() {
  const { hasItems, basketItemTypes, paymentVenue, userId } =
    useBasketService();

  const { usePaginated } = useUserBasedDiscountService();
  const { useUserTicketDiscounts } = usePublicTTDiscountPermissionService();

  const { discountRefs } = useUserTicketDiscounts(
    basketItemTypes,
    Number(paymentVenue)
  );

  // will initially get all ticket credits for the user, after items are added to the cart, will get applicable ticket credits
  const {
    data: ticketCredits,
    isLoading: ticketCreditsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePaginated({
    params: {
      venue: Number(paymentVenue),
      reference__in: discountRefs.toString(),
      page_size: 2,
    },
    options: {
      enabled:
        Boolean(paymentVenue) &&
        Boolean(discountRefs.length) &&
        Boolean(userId),
    },
  });

  const relevantTicketCredits = useMemo(() => {
    if (hasItems && !discountRefs.length) {
      return [];
    }
    if (ticketCredits) {
      return getFlattenedData(ticketCredits) ?? [];
    }
    return [];
  }, [ticketCredits, hasItems, discountRefs]);

  const hasRelevantTicketCredits = useMemo(() => {
    return Boolean(relevantTicketCredits.length);
  }, [relevantTicketCredits]);

  return {
    relevantTicketCredits,
    ticketCreditsLoading,
    hasItems,
    hasRelevantTicketCredits,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
